import type { APIResponse } from "./boot/api.types";
import { propGuard } from "./types";

//Formatters for displaying errors with the error code as the key corresponding
//to the formatter for that error code. Use fmt`display message string` to
//produce the correct formatter. Valid placeholders in the template string must
//be the following:
//  'path' - the API error detail path
//  'pathTail' - the last part of the API error detail path
//  'kind' - the API error detail kind
//  'status' - the API error status code
//  'code' - the API error detail code
//  'message' - the API error detail message
//  'requestId' - the API error requestId. If not present on the error then code will be used instead
//
//Any other string, number, or boolean value used as a placeholder will be
//displayed as is. Other values will be excluded.
const errorCodeDisplayFormaters = {
    //multiple
    multiple: fmt`Multiple errors were found.`,
    //authentication errors
    invalid_password_reset_token: fmt`Your password reset token is expired. Go to the login page and try forgot password again.`,
    invalid_totp: fmt`Invalid TOTP token. You must provide a valid TOTP token from your 2FA app.`,
    user_locked: fmt`Your account has been locked due to too many failed login attempts.`,
    unauthorized: fmt`You must login to perform this action.`,
    //authorization errors
    forbidden: fmt`You are not authorized to perform this action`,
    requires_permissions: fmt`${"message"}`,
    account_membership_not_allowed: fmt`You cannot perform this action on your own account.`,
    requires_account_membership: fmt`You do not have access to perform this action on the current account.`,
    //conflict errors
    conflict: fmt`Unable to complete your request due to a conflict - ${"message"}`,
    duplicate_entry: fmt`${"message"}`,
    //validation errors
    user_exists: fmt`A user with that email already exists.`,
    required: fmt`${"pathTail"} is required.`,
    empty_string: fmt`${"pathTail"} is required.`,
    invalid_length: fmt`${"pathTail"} does not meet the length requirement.`,
    out_of_range: fmt`${"pathTail"} is out of the acceptable range.`,
    invalid_phone_number: fmt`${"pathTail"} must be a valid phone number.`,
    invalid_url: fmt`${"pathTail"} must be a valid URL.`,
    invalid_email: fmt`${"pathTail"} must be a valid email address.`,
    custom: fmt`${"pathTail"} - ${"message"}`,
    //unexpected errors
    network_error: fmt`Unable to complete your request. Please check your internet connection.`,
    not_found: fmt`${(e: APIErrorLike) => (e.path ? getPathTail(e.path) : "Resource")} was not found`,
    unexpected: fmt`An unexpected error occured. Please contact support.${"requestId"}`,
    service_unavailable: fmt`This Service is temporarily unavailable.${"requestId"}`,
    unknown: fmt`Something went wrong. You may need to contact support.${"requestId"}`,
    axios_error: fmt`Something went wrong. You may need to contact support. ${"status"}`,
    method_not_allowed: fmt`Something went wrong. You may need to contact support. ${"status"}`,
    canceled: fmt``
} as const;

const appErrorCodeFormatters = {
    incorrect_config: fmt`The application is not configured correctly. Please contact support.`,
    parse_error: fmt`An error occurred while processing data from the server. Please contact support.`
} as const;
const defaultErrorCodeFormatters = {
    unknown: fmt`Something went wrong. You may need to contact support.`,
    canceled: fmt``
} as const;

export class ErrorCodeFormatter<T extends string> {
    private errorCodeDisplayHash: Record<T, ErrorFormatter>;
    constructor(errorCodeDisplayHash: Record<T, ErrorFormatter>) {
        this.errorCodeDisplayHash = errorCodeDisplayHash;
    }

    private isValidErrorCode(code: unknown): code is T {
        return typeof code === "string" && code in this.errorCodeDisplayHash;
    }
    format(error: unknown) {
        if (isAPIErrorLike(error) && this.isValidErrorCode(error.code)) {
            return this.errorCodeDisplayHash[error.code](error);
        }
        if (
            "unknown" in this.errorCodeDisplayHash &&
            typeof this.errorCodeDisplayHash.unknown === "function"
        ) {
            return this.errorCodeDisplayHash.unknown(error);
        }

        return defaultErrorCodeFormatters.unknown(error);
    }

    static fmt = fmt;
}
function getPathTail(path?: string) {
    path = path ?? "";
    const pathParts = path.split(".");
    path = pathParts[pathParts.length - 1];
    return path?.[0]
        ? `${path[0]?.toUpperCase()}${path.slice(1) || ""}`
        : "Value";
}

/*
function encodeErrorStack(e?: string) {
    return btoa(String.fromCodePoint(...new TextEncoder().encode(e ?? "")));
}
    */
function fmt(
    strings: TemplateStringsArray,
    ...keys: unknown[]
): ErrorFormatter {
    return (error: unknown) => {
        if (!isAPIErrorLike(error) || error instanceof AppError) {
            /*
            if (error instanceof Error || error instanceof AppError) {
                return `Something went wrong. You may need to contact support and provide the following Error Code: ${encodeErrorStack(error.stack)}`;
            }
            */
            return "Something went wrong. You may need to contact support.";
        }
        const strArr: (string | undefined)[] = [strings[0]];
        keys.forEach((key, i) => {
            let value = undefined;
            if (typeof key === "function") {
                const val: unknown = key(error);
                value = typeof val === "string" ? val : " ";
            } else if (key === "pathTail") {
                value = getPathTail(error.path);
            } else if (
                key === "path" ||
                key === "kind" ||
                key === "code" ||
                key === "message" ||
                key === "status" ||
                key === "requestId"
            ) {
                value = error[key];
                if (key === "requestId" || key === "code") {
                    value ??= error.code;
                    value = value ? ` Error Code: ${value}` : undefined;
                } else {
                    value = value ? `${value}` : undefined;
                }
            } else if (isValidTemplateExpression(key)) {
                value = `${key}`;
            }
            strArr.push(value, strings[i + 1]);
        });

        return strArr.join("");
    };
}

interface APIResponseError {
    status: number;
    requestId?: string;
    errors?: APIResponseErrorDetail[];
}
interface APIResponseErrorDetail {
    kind?: string;
    path?: string;
    code?: string;
    message?: string;
}
interface ValidatedAPIResponseErrorDetail {
    kind: string;
    code: string;
    path: string;
    message: string;
    display: string;
}

type APIResponseErrorDetailWithStatusAndId = Pick<APIResponseError, "status"> &
    Pick<APIResponseError, "requestId"> &
    APIResponseErrorDetail;
type APIErrorLike = APIResponseErrorDetailWithStatusAndId &
    Required<Pick<APIResponseErrorDetail, "code">> &
    object;
type ErrorFormatter = (error: unknown) => string;
export type APIErrorCode = keyof typeof errorCodeDisplayFormaters;

function isValidTemplateExpression(
    value?: unknown
): value is string | number | boolean | bigint {
    return ["string", "number", "boolean", "bigint"].includes(typeof value);
}
function isAPIErrorCode(code?: unknown): code is APIErrorCode {
    return typeof code === "string" && code in errorCodeDisplayFormaters;
}
function isAPIErrorLike(error: unknown): error is APIErrorLike {
    return (
        typeof error === "object" &&
        error !== null &&
        typeof (error as APIErrorLike)?.status === "number" &&
        typeof (error as APIErrorLike)?.code === "string"
    );
}
export interface BaseAPPErrorOptions {
    display?: string | undefined;
    cause?: unknown;
    code?: string | undefined;
}
export interface APIErrorOptions extends BaseAPPErrorOptions {
    status?: number;
    errors?: Partial<ErrorDetail>[];
    response?: Pick<APIResponse<APIResponseError>, "data" | "status">;
    code?: APIErrorCode;
}
class ErrorDetail {
    kind: string;
    code: APIErrorCode;
    path: string;
    message: string;
    display: string;

    constructor(d: ValidatedAPIResponseErrorDetail) {
        this.kind = d.kind;
        this.code = isAPIErrorCode(d.code) ? d.code : "unknown";
        this.path = d.path;
        this.message = d.message;
        this.display = d.display;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GetOptions<T> = T extends new (message: string, options: infer O) => any
    ? O
    : never;
export abstract class BaseAppError extends Error {
    static override name = "BaseAppError";
    override name = "BaseAppError";
    //status is here to make all AppError instances compatable with APIError when checking errors
    status = NaN;
    code: string;
    display: string;

    static formatter = new ErrorCodeFormatter<string>(
        defaultErrorCodeFormatters
    );
    constructor(
        message: string,
        { display = "", code = "unknown", cause }: BaseAPPErrorOptions = {}
    ) {
        super(message, { cause });
        this.code = code;
        const constructor = this.constructor as typeof BaseAppError;

        this.name = `${constructor.name}(${this.code.toUpperCase()})`;

        if (cause instanceof Error && !!this.stack) {
            this.stack = this.stack.replace(
                /^([^:]*):/,
                `${constructor.name}(${code.toUpperCase()}):`
            );
        }

        this.display = display || constructor.formatter.format(this);
    }

    /**
     * Creates an instance of the error class from an unknown value.
     * If the value is already an instance of the calling error class, it is returned as is.
     * If the value is not an instance of the calling error class, a new instance is created using the provided options and, if available, the defaultErrorType.
     * The defaultErrorType is used to create a specific error type if the unknown value does not match the calling error class.
     */
    // Overload 1: Called with a matching error instance -> Returns that instance exactly
    static fromUnknown<
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        C extends new (message: string, options: any) => any
    >(
        this: C,
        e: unknown,
        options?: GetOptions<C> & { message?: string }
    ): InstanceType<C>;

    // Overload 2: Called with a fallback error type -> Returns the instance or the fallback type
    static fromUnknown<
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        C extends new (message: string, options: any) => any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        D extends new (message: string, options: any) => DisplayableError
    >(
        this: C,
        e: unknown,
        defaultErrorType: D,
        options?: GetOptions<D> & { message?: string }
    ): InstanceType<C> | InstanceType<D>;

    // Single Implementation
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static fromUnknown<C extends new (message: string, options: any) => any>(
        this: C,
        e: unknown,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        arg2?: any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        arg3?: any
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ): any {
        if (e instanceof this) {
            return e;
        }

        const isFallback = typeof arg2 === "function";
        const defaultErrorType = isFallback ? arg2 : undefined;
        const runtimeOptions = (isFallback ? arg3 : arg2) ?? {};

        const message =
            runtimeOptions.message ??
            (e instanceof Error ? e.message : "Unknown App Error");
        const mergedOptions = { cause: e, ...runtimeOptions };

        if (defaultErrorType) {
            return new defaultErrorType(message, mergedOptions);
        }

        return new this(message, mergedOptions);
    }
}
export type AppErrorCode =
    | keyof typeof appErrorCodeFormatters
    | keyof typeof defaultErrorCodeFormatters;
export interface APPErrorOptions extends BaseAPPErrorOptions {
    code?: AppErrorCode;
}
export class AppError extends BaseAppError {
    static override name = "ApplicationError";
    override code: AppErrorCode = "unknown";
    static override formatter = new ErrorCodeFormatter(appErrorCodeFormatters);

    constructor(message: string, options: APPErrorOptions = {}) {
        super(message, options);
        this.code = options.code ?? "unknown";
    }
}

/**
 * Takes an unknown thrown value and returns it if it's an {@link DisplayableError} otherwise it turns it into an {@link AppError}.
 * @param e The unknown value that was thrown.
 * @param options The constructor options for an {@link AppError}
 * @returns {DisplayableError}
 */
export const errorFromUnknown: (
    e: unknown,
    options?: APPErrorOptions & { message?: string }
) => DisplayableError = (
    e: unknown,
    options: APPErrorOptions & { message?: string } = {}
) => {
    if (isDisplayableError(e)) {
        return e;
    }
    const error = AppError.fromUnknown(e, options);
    if (import.meta.env.QUASAR_DEV) {
        // oxlint-disable-next-line typescript/no-misused-spread
        console.warn("Caught Error Object: ", { ...error });
        console.warn("Caught Error: ", error);
    }

    return error;
};
export class APIError extends BaseAppError {
    static #defaultErrorCode: (parent: unknown) => APIErrorCode = parent => {
        if (typeof parent !== "object" || parent === null) {
            return "unknown";
        }
        if ("code" in parent && isAPIErrorCode(parent?.code)) {
            return parent.code;
        }
        const status = "status" in parent ? parent.status : undefined;
        switch (status) {
            case 502:
            case 503:
                return "service_unavailable";
            case 500:
                return "unexpected";
            case 405:
                return "method_not_allowed";
            case 404:
                return "not_found";
            case 403:
                return "forbidden";
            case 401:
                return "unauthorized";
            case -1:
                return "axios_error";
            case 0:
                return "network_error";
            default:
                return "unknown";
        }
    };
    static #defaultErrorKind = (parent: unknown) => {
        if (
            typeof parent !== "object" ||
            parent === null ||
            !("status" in parent) ||
            typeof parent.status !== "number"
        ) {
            return "unknown";
        }
        switch (parent.status) {
            case 403:
                return "authorization";
            case 401:
                return "authentication";
            case -1:
                return "axios";
            case 0:
                return "network";
            default:
                if (parent.status >= 500) {
                    return "unexpected";
                }
                return "unknown";
        }
    };
    static #defaultErrorMessage = (parent?: unknown) => {
        if (
            typeof parent !== "object" ||
            parent === null ||
            !("status" in parent) ||
            typeof parent.status !== "number"
        ) {
            return "Unknown API Error";
        }
        switch (parent?.status) {
            case 502:
            case 503:
                return "Service Temporarily Unavailable";
            case 500:
                return "Internal Server Error";
            case 405:
                return "Method Not Allowed";
            case 404:
                return "Resource Not Found";
            case 403:
                return "Forbidden Operation";
            case 401:
                return "Invalid Credentials or Invalid Bearer Token";
            case -1:
                return "Internal Error";
            case 0:
                return "No response received. Internet may be unavailable";
            default:
                return "Unknown API Error";
        }
    };
    static #parseAPIResponseErrorDetail = (
        e: unknown,
        status: number,
        requestId: string
    ): ValidatedAPIResponseErrorDetail => {
        const code =
            e !== null &&
            typeof e === "object" &&
            "code" in e &&
            typeof e.code === "string" &&
            isAPIErrorCode(e.code)
                ? e.code
                : APIError.#defaultErrorCode({ status });
        const err =
            e !== null && typeof e === "object"
                ? { ...e, status, requestId, code }
                : { status, requestId, code };

        const errorMessage = propGuard(err, "error", "string")
            ? err.error || undefined
            : undefined;
        const message = propGuard(err, "message", "string")
            ? err.message || undefined
            : undefined;
        const display = APIError.formatter.format(err);

        return {
            kind: propGuard(err, "kind", "string")
                ? err.kind
                : APIError.#defaultErrorKind(err),
            code:
                propGuard(err, "code", "string") && !!err.code
                    ? err.code
                    : APIError.#defaultErrorCode(err),
            path: propGuard(err, "path", "string") ? err.path : "",
            message:
                errorMessage ?? message ?? APIError.#defaultErrorMessage(err),
            display
        };
    };

    static #stringifyError(error: ValidatedAPIResponseErrorDetail) {
        return `${
            error.path ? `${error.path}: ` : ""
        }(${error.code.toUpperCase()})${!error.path && error.message ? ":" : ""}${
            error.message ? ` ${error.message}` : ""
        }`;
    }
    static override name = "APIError";
    override code: APIErrorCode = "unknown";
    static override formatter = new ErrorCodeFormatter(
        errorCodeDisplayFormaters
    );
    errors: ErrorDetail[] = [];
    requestId: string;
    response?: Partial<APIResponse<APIResponseError>> | undefined;

    constructor(
        message: string,
        { status, errors, response, display, cause, code }: APIErrorOptions
    ) {
        status ??= response?.status ?? NaN;
        const requestId = response?.data?.requestId ?? "";
        const responseErrors = Array.isArray(response?.data?.errors)
            ? response?.data?.errors
            : [];
        const unvalidatedErrors = Array.isArray(errors)
            ? errors
            : responseErrors;
        const validatedErrors: ErrorDetail[] = [];
        const errorStrings = [];
        for (const e of unvalidatedErrors) {
            const error = APIError.#parseAPIResponseErrorDetail(
                e,
                status,
                requestId
            );
            validatedErrors.push(new ErrorDetail(error));
            errorStrings.push(APIError.#stringifyError(error));
        }
        if (validatedErrors.length > 1) {
            message = `${message}${message ? "\n" : ""}{
                ${errorStrings.join(",\n")}
            }`;
            code = "multiple";
            display ??= APIError.formatter.format({ status, code });
        } else {
            const mainError =
                validatedErrors[0] ||
                new ErrorDetail(
                    APIError.#parseAPIResponseErrorDetail(
                        undefined,
                        status,
                        requestId
                    )
                );
            message ??= mainError.message;
            display ??=
                mainError.display ||
                APIError.formatter.format({
                    status,
                    code: mainError.code
                });
            code ??= mainError.code;
        }
        super(message, { display, cause, code });
        this.code = code;
        this.status = status;
        this.requestId = requestId;
        this.errors = validatedErrors;
        this.response = response;
        if (import.meta.env.QUASAR_DEV) {
            if (code === "canceled") {
                console.warn(this.message);
            } else {
                console.error(this);
            }
        }
    }

    static getDisplayMessage(e: unknown) {
        if (e instanceof APIError || e instanceof ErrorDetail) {
            return e.display;
        }
        return APIError.formatter.format(e);
    }
}

export type DisplayableError = BaseAppError;
export function isDisplayableError(error: unknown): error is DisplayableError {
    return error instanceof BaseAppError;
}
